/**
 * @file All permissions checks related to topics
 */

import { TopicWithMembersFragment } from '../../generated/graphql';
import { PermissionsTopic } from '../../models/permissions';
import { TopicPermissionCheckMember } from '../../models/topic';
import { reportError } from '../../services/reporting';

import {
  TOPIC__ADMIN,
  TOPIC__CREATE,
  TOPIC__DELETE,
  TOPIC__UPDATE,
  TOPIC__VIEW,
} from './permissionsAll';
import { getIsBasic, getPermissionsCurrent, getUserCurrent } from './storage';

type TopicCreator = TopicWithMembersFragment['creator'];

/**
 *
 * HELPER
 *
 */

/**
 * Check whether the user has a specific permission
 *
 * @param which Which permission we want to check against
 * @returns     Whether it's true
 */
const hasPermission = (which: PermissionsTopic): boolean => {
  return getPermissionsCurrent().includes(which);
};

/**
 * Check whether the current user has admin permissions for topics
 *
 * @returns Whether it's true
 */
export const getIsAdmin = (): boolean => {
  return getPermissionsCurrent().includes(TOPIC__ADMIN);
};

/**
 * Check whether the current user is author of a specific topic
 *
 * @param topic The topic to check permissions for
 * @returns     Whether it's true
 */
const getIsAuthor = (topic: TopicCreator): boolean => {
  // Will happen for auto-generated topics
  // Which will happen when concierge form is posted 1st time
  if (topic === null || topic === undefined) {
    return false;
  }

  const user = getUserCurrent();
  return user !== null && user.id === topic.id;
};

/**
 * Check whether the current user is a member of a specified topic
 *
 * @param topic The topic to check
 * @returns     Whether it's true
 */
export const getIsMember = (topic: TopicPermissionCheckMember): boolean => {
  const user = getUserCurrent();

  if (user === null) {
    reportError('getIsMember: Reading user current before initializing it.');
    return false;
  }

  if (topic.members.findIndex(member => member.id === user.id) !== -1) {
    return true;
  }

  if (topic.isPrivate === false) {
    return true;
  }

  return (
    topic.groups.filter(group =>
      group.members.some(member => member.id === user.id),
    ).length !== 0
  );
};

/**
 *
 * CREATE
 *
 */

/**
 * Get whether the user can access a topic; Either because they're
 * - A member directly
 * - A member of a group that is added to the topic
 * - An admin (they can access all topics)
 *
 * @param topic The topic to check for
 * @returns     Whether the user can access the topic
 */
export const getCanAccess = (topic: TopicWithMembersFragment): boolean => {
  return topic.isPrivate === false || getIsAdmin() || getIsMember(topic);
};

/**
 * Check whether the user is allowed to add members to a topic
 *
 * @param topic The topic to check for
 * @returns     Whether the user has permissions
 */
export const getCanAddMember = (topic: TopicWithMembersFragment): boolean => {
  if (topic.isPrivate === false) {
    return false;
  }

  if (getIsAdmin()) {
    return true;
  }

  if (hasPermission(TOPIC__UPDATE) === false) {
    return false;
  }

  return getIsAuthor(topic.creator) || getIsMember(topic);
};

/**
 * Check whether the user has permissions
 * to create a topic
 *
 * @returns Whether the user has permissions
 */
export const getCanCreateTopic = (): boolean => {
  if (getIsBasic() === true) {
    return false;
  }

  return getIsAdmin() || hasPermission(TOPIC__CREATE);
};

/**
 *
 * READ
 *
 */

/**
 * Check whether the current user can view a topic's members
 *
 * @param topic The topic to check permissions for
 * @returns     Whether the user has permissions
 */
export const getCanViewMembers = (topic: TopicWithMembersFragment): boolean => {
  if (getIsAdmin()) {
    return true;
  }

  return (
    hasPermission(TOPIC__VIEW) &&
    (getIsAuthor(topic.creator) || getIsMember(topic))
  );
};

/**
 *
 * UPDATE
 *
 */

/**
 * Check whether a user is allowed to update topic data
 * (title, description...)
 *
 * @param topic The topic creator to check for
 * @returns     Whether the user has permissions
 */
export const getCanUpdateTopic = (topic: TopicWithMembersFragment): boolean => {
  if (getIsAdmin()) {
    return true;
  }

  if (hasPermission(TOPIC__UPDATE) === false) {
    return false;
  }

  return getIsAuthor(topic.creator) || (topic.isPrivate && getIsMember(topic));
};

/**
 *
 * DELETE
 *
 */

/**
 * Check whether the user is allowed to delete a specific topic
 *
 * @param topic The topic creator to check for
 * @returns     Whether the user has permissions
 */
export const getCanDeleteTopic = (topic: TopicWithMembersFragment): boolean => {
  return (
    getIsAdmin() || (getIsAuthor(topic.creator) && hasPermission(TOPIC__DELETE))
  );
};
